import { db } from '../db/db.js';
import type { Subscription } from '../db/schema.js';
import type { InventoryEvent, SubscriptionCreatedPayload } from '../events/types.js';
import { computeSubscriptionHealth, type SubscriptionHealth } from './subscription-health.js';

export interface SubscriptionRow {
  sub: Subscription;
  health: SubscriptionHealth;
  /** 從 sub.nextDelivery 算（負值 = 已過 expected ETA） */
  daysUntilNextDelivery: number;
  /** 該 sub 對應的 ShoppingItem 歷史筆數（任何 status） */
  historyCount: number;
}

export interface SubscriptionView {
  active: SubscriptionRow[];
  inactive: SubscriptionRow[];
  counts: { active: number; inactive: number; total: number };
}

const DAY_MS = 86_400_000;

export async function loadSubscriptionView(now: number = Date.now()): Promise<SubscriptionView> {
  const subs = await db.subscriptions.toArray();
  if (subs.length === 0) {
    return {
      active: [],
      inactive: [],
      counts: { active: 0, inactive: 0, total: 0 },
    };
  }
  const allShop = await db.shoppingItems.toArray();
  // 一次撈 subscription_created events 取 createdAt（v0.4 schema 沒在 Subscription row 上保留）
  const createdEvents = await db.events.where('type').equals('subscription_created').toArray();
  const createdAtById = new Map<string, number>();
  for (const ev of createdEvents) {
    const p = ev.payload as SubscriptionCreatedPayload;
    createdAtById.set(p.subscriptionId, p.createdAt);
  }

  const rows: SubscriptionRow[] = subs.map((sub) => {
    const history = allShop.filter((r) => r.subscriptionId === sub.id);
    const health = computeSubscriptionHealth(sub, history, now, createdAtById.get(sub.id));
    const daysUntilNextDelivery = Math.ceil((sub.nextDelivery - now) / DAY_MS);
    return { sub, health, daysUntilNextDelivery, historyCount: history.length };
  });

  const active = rows
    .filter((r) => r.sub.active)
    .sort((a, b) => a.sub.nextDelivery - b.sub.nextDelivery);
  const inactive = rows
    .filter((r) => !r.sub.active)
    .sort((a, b) => b.sub.nextDelivery - a.sub.nextDelivery);

  return {
    active,
    inactive,
    counts: { active: active.length, inactive: inactive.length, total: rows.length },
  };
}

// re-export 給 main.ts 用
export type { InventoryEvent };
